// ─────────────────────────────────────────────────────────────────────────────
// «Enviar también a Brillamos» avisa (decisión 6 de las reglas de envío por campaña, 2026-10-15).
//
// El servidor (`CreateRecognitionCommandHandler`) adjunta la campaña Brillamos destino —la próxima
// programada para la OU del emisor, la misma que trae `useNextBrillamos`— solo si el reconocimiento cabe
// en sus reglas de envío (`MultiEnvioPolicy.Cabe`); si no, lo guarda como diario sin decir nada. Aquí se
// avisa antes de enviar con las reglas de ESA campaña para el emisor (`recognition-rules` con su
// `campaignId`, que aplica la misma herencia que el servidor). No bloquea: quien envía decide.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import { pickLocalized, useRecognitionRules } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';

/** Centinela de «sin tope» en `maxFundamentalsPerRecognition` (el servidor usa `int.MaxValue`). */
export const SIN_TOPE_FUNDAMENTALES = 2147483647;

interface ReglasDestino { multinomination?: boolean; maxFundamentalsPerRecognition?: number }

/** Los límites de la campaña destino que el envío supera; se devuelven todos los que aplican para que el
 *  aviso los enseñe juntos. `null` si cabe o si las reglas aún no se conocen (no se avisa a ciegas). */
export function limitesExcedidos(
  reglas: ReglasDestino | undefined, personas: number, fundamentales: number,
): { personas?: number; fundamentales?: number } | null {
  if (reglas?.multinomination === undefined && reglas?.maxFundamentalsPerRecognition === undefined) return null;
  const maxPersonas = reglas.multinomination === false ? 1 : undefined;
  const tope = reglas.maxFundamentalsPerRecognition;
  const maxFundamentales = tope !== undefined && tope < SIN_TOPE_FUNDAMENTALES ? tope : undefined;
  const excede = (maxPersonas !== undefined && personas > maxPersonas)
    || (maxFundamentales !== undefined && fundamentales > maxFundamentales);
  if (!excede) return null;
  return {
    ...(maxPersonas !== undefined && { personas: maxPersonas }),
    ...(maxFundamentales !== undefined && { fundamentales: maxFundamentales }),
  };
}

/** Texto del aviso previo, o `null` si no hay nada que avisar. Las reglas de la campaña destino solo se
 *  piden con la casilla marcada. */
export function useAvisoEnvioBrillamos(
  destino: CampaignDto | null | undefined, marcado: boolean, personas: number, fundamentales: number,
): string | null {
  const { t, i18n } = useTranslation('recognition');
  const activo = marcado && Boolean(destino);
  const reglas = useRecognitionRules(destino?.id, 'Brillamos', activo);
  if (!activo || !destino) return null;
  const limites = limitesExcedidos(reglas.data, personas, fundamentales);
  if (!limites) return null;
  const partes = [
    limites.personas !== undefined && t('panel.brillamosMaxPersonas', { count: limites.personas }),
    limites.fundamentales !== undefined && t('panel.brillamosMaxFundamentales', { count: limites.fundamentales }),
  ].filter(Boolean);
  return t('panel.sendToBrillamosNoCabe', { campana: pickLocalized(destino.nombre, i18n.language), limites: partes.join(' / ') });
}

/**
 * Aviso posterior: si la casilla iba marcada y el servidor no adjuntó la campaña destino (no cabía en sus
 * reglas, o la campaña no acepta reconocimientos desde Fundamentales), el nombre de esa campaña para el
 * mensaje de éxito; si no, `null`. Se resuelve al recibir la respuesta, no al pintar.
 */
export function campanaNoAdjuntada(
  enviado: { sendToBrillamos?: boolean }, creado: { campanaSecundariaId?: string | null } | undefined,
  destino: CampaignDto | null | undefined, lang: string,
): string | null {
  if (!enviado.sendToBrillamos || creado?.campanaSecundariaId || !destino) return null;
  return pickLocalized(destino.nombre, lang);
}
